// Libs
import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useRef, useState } from 'react';
// Others
import { useAppDispatch } from '~/utils/redux/hooks';
import {
  downloadQuizAnswersExportFile,
  getCurrentQuizAnswersExport,
  startQuizAnswersExport,
} from '~/thunks/quizAnswersExport/quizAnswersExportThunk';
import {
  DISCUSSION_EXPORT_NOTICE_MS,
  QUIZ_ANSWERS_EXPORT_POLL_INTERVAL_MS,
  QUIZ_ANSWERS_EXPORT_POLL_MAX_INTERVAL_MS,
  QUIZ_ANSWERS_EXPORT_QUEUED_POLL_INTERVAL_MS,
  TIMEOUT_ALERT_MILLISECOND,
} from '~/utils/constants/common';
import { QuizAnswersExportPhaseEnum, QuizAnswersExportStatusEnum } from '~/utils/enum/common';
import { IQuizAnswersExportJob } from '~/utils/interface/quizAnswersExport';
import { IToast } from '~/utils/interface/toast';
import { toastActions } from '~/thunks/toast/toastSlice';
import { generateGuid } from '~/utils/helper/common';

export const QUIZ_ANSWERS_EXPORT_NAMESPACE = 'admin/quizAnswersExport';
const NAMESPACE = QUIZ_ANSWERS_EXPORT_NAMESPACE;

// Business codes returned by the backend (QUIZ_ANSWERS_EXPORT block + course not found).
const ERROR_MESSAGE_KEYS: Record<number, string> = {
  15001: 'quiz_answers_export_error_not_found',
  15002: 'quiz_answers_export_error_not_ready',
  15003: 'quiz_answers_export_error_canvas_token',
  15004: 'quiz_answers_export_error_canvas_permission',
  15005: 'quiz_answers_export_error_canvas_unavailable',
  15006: 'quiz_answers_export_error_student_not_enrolled',
  15007: 'quiz_answers_export_error_pdf_render',
  80001: 'quiz_answers_export_error_course_not_found',
};

const getErrorCode = (error: unknown): number | undefined => {
  const value = error as { code?: unknown; response?: { data?: { code?: unknown } } } | undefined;
  const code = typeof value?.code === 'number' ? value.code : value?.response?.data?.code;
  return typeof code === 'number' ? code : undefined;
};

type Selection = {
  /** Canvas user id of the student chosen in the Exam Area form. */
  studentId?: string;
  /** Course chosen in the same form. */
  courseId?: string;
};

/**
 * "Risposte Quiz PDF" of the Exam Area: the backend builds the PDF of the student and course chosen
 * in the form. The export started from the page is downloaded automatically when ready; the
 * admin's last export (one at a time, kept one hour) stays available with "Scarica PDF".
 */
export const useQuizAnswersExport = (selection: Selection) => {
  const { studentId, courseId } = selection;
  const { t } = useTranslation([NAMESPACE]);
  const dispatch = useAppDispatch();

  const [job, setJob] = useState<IQuizAnswersExportJob | null>(null);
  const [isStarting, setIsStarting] = useState<boolean>(false);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const isMountedRef = useRef<boolean>(true);
  const pollTimerRef = useRef<ReturnType<typeof setTimeout>>();
  // The running job the page follows, and the one it started (downloaded when done).
  const trackedJobIdRef = useRef<string | null>(null);
  const autoDownloadJobIdRef = useRef<string | null>(null);

  const isRunning = job?.status === QuizAnswersExportStatusEnum.RUNNING;

  const showToast = useCallback(
    (type: IToast['type'], message: string, durationMs: number = TIMEOUT_ALERT_MILLISECOND) => {
      const messageId = generateGuid();
      dispatch(
        toastActions.addToast({ id: messageId, type, message, allowClose: durationMs > TIMEOUT_ALERT_MILLISECOND })
      );
      setTimeout(() => {
        dispatch(toastActions.removeToast(messageId));
      }, durationMs);
    },
    [dispatch]
  );

  const getErrorMessage = useCallback(
    (code?: number) => t(ERROR_MESSAGE_KEYS[code ?? 0] ?? 'quiz_answers_export_error_generic', { ns: NAMESPACE }),
    [t]
  );

  const downloadJob = useCallback(
    async (current: IQuizAnswersExportJob, automatic: boolean) => {
      if (!current.fileName) return;
      setIsDownloading(true);
      try {
        await dispatch(downloadQuizAnswersExportFile({ jobId: current.id, fileName: current.fileName })).unwrap();
        if (automatic) {
          showToast(
            current.complete ? 'success' : 'warning',
            t(current.complete ? 'quiz_answers_export_done_toast' : 'quiz_answers_export_done_incomplete_toast', {
              ns: NAMESPACE,
            }),
            current.complete ? TIMEOUT_ALERT_MILLISECOND : DISCUSSION_EXPORT_NOTICE_MS
          );
        }
      } catch (error) {
        const code = getErrorCode(error);
        showToast('error', code ? getErrorMessage(code) : t('quiz_answers_export_download_error', { ns: NAMESPACE }));
      } finally {
        if (isMountedRef.current) setIsDownloading(false);
      }
    },
    [dispatch, getErrorMessage, showToast, t]
  );

  /** Applies a status read from the backend; true while the export still runs. */
  const applyJob = useCallback(
    (current: IQuizAnswersExportJob | null) => {
      const trackedJobId = trackedJobIdRef.current;
      if (trackedJobId && current?.id !== trackedJobId) {
        // The export we were following is gone: the server restarted (e.g. a deploy).
        trackedJobIdRef.current = null;
        autoDownloadJobIdRef.current = null;
        setJob(null);
        showToast('warning', t('quiz_answers_export_interrupted', { ns: NAMESPACE }), DISCUSSION_EXPORT_NOTICE_MS);
        return false;
      }

      setJob(current);
      if (!current) return false;
      if (current.status === QuizAnswersExportStatusEnum.RUNNING) {
        trackedJobIdRef.current = current.id;
        return true;
      }

      trackedJobIdRef.current = null;
      if (autoDownloadJobIdRef.current === current.id) {
        autoDownloadJobIdRef.current = null;
        if (current.status === QuizAnswersExportStatusEnum.DONE) downloadJob(current, true);
        if (current.status === QuizAnswersExportStatusEnum.FAILED) {
          showToast(
            'error',
            `${t('quiz_answers_export_failed', { ns: NAMESPACE })}: ${getErrorMessage(current.error?.code)}`,
            DISCUSSION_EXPORT_NOTICE_MS
          );
        }
      }
      return false;
    },
    [downloadJob, getErrorMessage, showToast, t]
  );

  const schedulePoll = useCallback(
    (delay: number) => {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = setTimeout(async () => {
        try {
          const data = await dispatch(getCurrentQuizAnswersExport()).unwrap();
          if (!isMountedRef.current) return;
          const current = data?.job ?? null;
          if (applyJob(current)) {
            schedulePoll(
              current?.phase === QuizAnswersExportPhaseEnum.QUEUED
                ? QUIZ_ANSWERS_EXPORT_QUEUED_POLL_INTERVAL_MS
                : QUIZ_ANSWERS_EXPORT_POLL_INTERVAL_MS
            );
          }
        } catch {
          // Backend unreachable (e.g. restarting): retry later, no toast.
          if (isMountedRef.current) schedulePoll(Math.min(delay * 2, QUIZ_ANSWERS_EXPORT_POLL_MAX_INTERVAL_MS));
        }
      }, delay);
    },
    [dispatch, applyJob]
  );

  const startExport = async () => {
    if (isStarting || isRunning) return;
    if (!studentId || !courseId) {
      showToast('warning', t('quiz_answers_export_select_student_course', { ns: NAMESPACE }));
      return;
    }
    setIsStarting(true);
    // The previous PDF (maybe of another student) is replaced: hide it while the new one starts.
    setJob(null);
    try {
      const data = await dispatch(
        startQuizAnswersExport({ studentId: String(studentId), courseId: String(courseId) })
      ).unwrap();
      const started = data?.job ?? null;
      // One export at a time per admin: the backend returns the one still running (e.g. from another tab).
      if (started && started.filters.studentId !== String(studentId)) {
        showToast(
          'warning',
          t('quiz_answers_export_other_student_running', {
            ns: NAMESPACE,
            name: started.filters.studentName || started.filters.studentId,
          }),
          DISCUSSION_EXPORT_NOTICE_MS
        );
      } else if (started) {
        autoDownloadJobIdRef.current = started.id;
      }
      if (applyJob(started)) schedulePoll(QUIZ_ANSWERS_EXPORT_POLL_INTERVAL_MS);
    } catch (error) {
      showToast('error', getErrorMessage(getErrorCode(error)));
    } finally {
      if (isMountedRef.current) setIsStarting(false);
    }
  };

  const downloadFile = () => {
    if (job && !isDownloading) downloadJob(job, false);
  };

  useEffect(() => {
    isMountedRef.current = true;
    // An export started earlier (or from another tab) is resumed, or offered again for download.
    dispatch(getCurrentQuizAnswersExport())
      .unwrap()
      .then((data) => {
        if (isMountedRef.current && applyJob(data?.job ?? null)) {
          schedulePoll(QUIZ_ANSWERS_EXPORT_POLL_INTERVAL_MS);
        }
      })
      .catch(() => {});

    return () => {
      isMountedRef.current = false;
      clearTimeout(pollTimerRef.current);
    };
  }, [dispatch, applyJob, schedulePoll]);

  const getPhaseLabel = (current: IQuizAnswersExportJob) => {
    switch (current.phase) {
      case QuizAnswersExportPhaseEnum.COLLECTING:
        return current.progress.quizzesTotal
          ? t('quiz_answers_export_phase_collecting', {
              ns: NAMESPACE,
              done: current.progress.quizzesDone,
              total: current.progress.quizzesTotal,
            })
          : t('quiz_answers_export_phase_preflight', { ns: NAMESPACE });
      case QuizAnswersExportPhaseEnum.QUEUED:
        return t('quiz_answers_export_phase_queued', { ns: NAMESPACE });
      case QuizAnswersExportPhaseEnum.RENDERING:
      case QuizAnswersExportPhaseEnum.FINISHED:
        return t('quiz_answers_export_phase_rendering', { ns: NAMESPACE });
      default:
        return t('quiz_answers_export_phase_preflight', { ns: NAMESPACE });
    }
  };

  return {
    job,
    isStarting,
    isRunning,
    isDownloading,
    startExport,
    downloadFile,
    getPhaseLabel,
    getErrorMessage,
  };
};

export type QuizAnswersExportState = ReturnType<typeof useQuizAnswersExport>;
